import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, chatApi, chatFilesApi, getWebSocketUrl } from '../lib/api';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  MessageCircle, X, Send, Loader2, ChevronLeft, Plus, Star, Paperclip, FileText, Image,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const STATUS_COLORS = {
  OPEN: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  WAITING_USER: 'bg-orange-100 text-orange-700',
  CLOSED: 'bg-slate-100 text-slate-500',
};

// Notification sound
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 800;
    osc.type = 'sine';
    gain.gain.value = 0.1;
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  } catch (e) { /* ignore */ }
}

export function ChatWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [typing, setTyping] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const shouldHide = !user || user.role === 'SUPPORT_AGENT';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch chats
  const fetchChats = async () => {
    try {
      const { data } = await chatApi.list({ limit: 50 });
      setChats(data.items || []);
    } catch (e) { /* ignore */ }
  };

  // Fetch unread count
  const fetchUnread = async () => {
    try {
      const { data } = await chatApi.getUnreadCount();
      setUnreadTotal(data.unread || 0);
    } catch (e) { /* ignore */ }
  };

  // Fetch messages for active chat
  const fetchMessages = async (chatId) => {
    try {
      const { data } = await chatApi.getMessages(chatId, { limit: 200 });
      setMessages(data.items || []);
      setTimeout(scrollToBottom, 100);
      // Mark as read
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'mark_read', chat_id: chatId }));
      }
    } catch (e) { /* ignore */ }
  };

  // WebSocket connection
  useEffect(() => {
    if (!user || shouldHide) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const connect = () => {
      const ws = new WebSocket(getWebSocketUrl(token));
      wsRef.current = ws;

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          const msg = data.message;
          setMessages(prev => {
            if (prev.some(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          setTimeout(scrollToBottom, 100);
          // Play sound only for non-closed chats
          if (msg.sender_id !== user.id && msg.message_type !== 'SYSTEM') {
            playNotificationSound();
          }
          fetchUnread();
          fetchChats();
        } else if (data.type === 'typing') {
          setTyping(data.user_name);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTyping(null), 2000);
        } else if (data.type === 'messages_read') {
          setMessages(prev => prev.map(m => m.sender_id === user.id ? { ...m, status: 'READ' } : m));
        } else if (data.type === 'chat_status_changed') {
          fetchChats();
          fetchUnread();
          if (data.message) {
            setMessages(prev => {
              if (prev.some(m => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
          }
        }
      };

      ws.onclose = () => {
        setTimeout(connect, 3000);
      };
    };

    connect();
    fetchUnread();

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [user, shouldHide]);

  // Fetch chats when widget opens
  useEffect(() => {
    if (isOpen) fetchChats();
  }, [isOpen]);

  // Send message — try WS first, REST fallback
  const handleSend = async () => {
    if (!newMessage.trim() || !activeChat) return;
    const msgText = newMessage.trim();
    setNewMessage('');
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'send_message',
        chat_id: activeChat.id,
        message: msgText,
      }));
    } else {
      // REST fallback
      try {
        const { data } = await chatApi.sendMessage(activeChat.id, { message: msgText });
        setMessages(prev => {
          if (prev.some(m => m.id === data.id)) return prev;
          return [...prev, data];
        });
        setTimeout(scrollToBottom, 100);
      } catch (e) {
        toast.error('Failed to send message');
        setNewMessage(msgText);
      }
    }
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeChat || !wsRef.current) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10 MB)'); return; }
    setUploading(true);
    try {
      const { data } = await chatFilesApi.upload(file);
      wsRef.current.send(JSON.stringify({
        type: 'send_message',
        chat_id: activeChat.id,
        message: file.name,
        message_type: 'FILE',
        file_url: data.id,
        file_name: data.original_filename,
      }));
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Send typing indicator
  const handleTyping = () => {
    if (activeChat && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', chat_id: activeChat.id }));
    }
  };

  // Create new chat
  const handleCreateChat = async () => {
    if (!subject.trim()) return;
    setSending(true);
    try {
      const { data } = await chatApi.create({ subject: subject.trim() });
      setShowNewChat(false);
      setSubject('');
      setActiveChat(data);
      fetchMessages(data.id);
      fetchChats();
    } catch (e) {
      toast.error('Failed to create chat');
    } finally {
      setSending(false);
    }
  };

  // Open a chat
  const openChat = (chat) => {
    setActiveChat(chat);
    fetchMessages(chat.id);
    setShowRating(false);
  };

  // Rate chat
  const handleRate = async () => {
    if (!rating || !activeChat) return;
    try {
      await chatApi.rate(activeChat.id, { rating, feedback });
      toast.success('Thank you for your feedback!');
      setShowRating(false);
      fetchChats();
    } catch (e) { /* ignore */ }
  };

  // Don't render for support agents (they have their own dashboard) or unauthenticated
  if (shouldHide) return null;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-widget-toggle"
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-[#005DE8] text-white shadow-lg hover:bg-[#0047B3] transition-all flex items-center justify-center"
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!isOpen && unreadTotal > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
            {unreadTotal > 9 ? '9+' : unreadTotal}
          </span>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-[9999] w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="chat-widget-panel">
          {/* Header */}
          <div className="bg-[#005DE8] text-white px-4 py-3 flex items-center gap-2">
            {activeChat && (
              <button onClick={() => { setActiveChat(null); setMessages([]); setShowRating(false); }} className="p-1 hover:bg-white/20 rounded">
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <h3 className="font-semibold text-sm flex-1" style={{ fontFamily: 'Outfit' }}>
              {activeChat ? activeChat.subject : 'Support Chat'}
            </h3>
            {activeChat && (
              <Badge className={cn('text-[10px] px-1.5 py-0.5', STATUS_COLORS[activeChat.status])}>
                {activeChat.status?.replace('_', ' ')}
              </Badge>
            )}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {!activeChat && !showNewChat ? (
              /* Chat List */
              <div className="p-3 space-y-2">
                <Button
                  onClick={() => setShowNewChat(true)}
                  className="w-full bg-[#005DE8] hover:bg-[#0047B3] text-sm"
                  data-testid="new-chat-button"
                >
                  <Plus className="h-4 w-4 mr-2" /> New Conversation
                </Button>
                {chats.length === 0 ? (
                  <p className="text-center text-slate-400 text-sm py-8">No conversations yet</p>
                ) : (
                  chats.map(chat => (
                    <button
                      key={chat.id}
                      onClick={() => openChat(chat)}
                      data-testid={`chat-item-${chat.id}`}
                      className="w-full text-left p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-slate-900 truncate flex-1">{chat.subject}</span>
                        {chat.unread_user > 0 && (
                          <span className="ml-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">{chat.unread_user}</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-slate-400 truncate">{chat.last_message || 'No messages yet'}</span>
                        <Badge className={cn('text-[10px] ml-2 shrink-0', STATUS_COLORS[chat.status])}>{chat.status?.replace('_', ' ')}</Badge>
                      </div>
                      {chat.last_message_at && (
                        <span className="text-[10px] text-slate-300 mt-0.5 block">
                          {formatDistanceToNow(new Date(chat.last_message_at), { addSuffix: true })}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            ) : showNewChat ? (
              /* New Chat Form */
              <div className="p-4 space-y-3">
                <p className="text-sm text-slate-600">What do you need help with?</p>
                <Input
                  placeholder="e.g., Invoice issue, Warranty question..."
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreateChat()}
                  data-testid="new-chat-subject"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowNewChat(false)} className="flex-1">Cancel</Button>
                  <Button size="sm" onClick={handleCreateChat} disabled={!subject.trim() || sending} className="flex-1 bg-[#005DE8]" data-testid="start-chat-button">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Chat'}
                  </Button>
                </div>
              </div>
            ) : (
              /* Messages */
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {messages.map(msg => (
                    <div key={msg.id} className={cn('flex', msg.sender_type === 'SYSTEM' ? 'justify-center' : msg.sender_id === user.id ? 'justify-end' : 'justify-start')}>
                      {msg.message_type === 'SYSTEM' ? (
                        <span className="text-[11px] text-slate-400 bg-slate-50 px-2 py-1 rounded-full">{msg.message}</span>
                      ) : msg.message_type === 'FILE' ? (
                        <div className={cn(
                          'max-w-[80%] px-3 py-2 rounded-2xl text-sm',
                          msg.sender_id === user.id ? 'bg-[#005DE8] text-white rounded-br-md' : 'bg-slate-100 text-slate-900 rounded-bl-md'
                        )}>
                          {msg.sender_id !== user.id && <p className="text-[10px] font-medium mb-0.5 opacity-70">{msg.sender_name}</p>}
                          {msg.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            <img src={chatFilesApi.getUrl(msg.file_url)} alt={msg.file_name} className="max-w-[200px] rounded-lg mb-1 cursor-pointer" onClick={() => window.open(chatFilesApi.getUrl(msg.file_url), '_blank')} />
                          ) : (
                            <a href={chatFilesApi.getUrl(msg.file_url)} target="_blank" rel="noopener noreferrer" className={cn('flex items-center gap-2 px-2 py-1.5 rounded-lg', msg.sender_id === user.id ? 'bg-white/20 hover:bg-white/30' : 'bg-white hover:bg-slate-50 border')}>
                              <FileText className="h-4 w-4 shrink-0" />
                              <span className="text-xs truncate">{msg.file_name || 'File'}</span>
                            </a>
                          )}
                          <div className={cn('flex items-center justify-end gap-1 mt-0.5', msg.sender_id === user.id ? 'text-blue-200' : 'text-slate-400')}>
                            <span className="text-[9px]">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            {msg.sender_id === user.id && <span className="text-[9px]">{msg.status === 'READ' ? '✓✓' : '✓'}</span>}
                          </div>
                        </div>
                      ) : (
                        <div className={cn(
                          'max-w-[80%] px-3 py-2 rounded-2xl text-sm',
                          msg.sender_id === user.id
                            ? 'bg-[#005DE8] text-white rounded-br-md'
                            : 'bg-slate-100 text-slate-900 rounded-bl-md'
                        )}>
                          {msg.sender_id !== user.id && (
                            <p className="text-[10px] font-medium mb-0.5 opacity-70">{msg.sender_name}</p>
                          )}
                          <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                          <div className={cn('flex items-center justify-end gap-1 mt-0.5', msg.sender_id === user.id ? 'text-blue-200' : 'text-slate-400')}>
                            <span className="text-[9px]">
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {msg.sender_id === user.id && (
                              <span className="text-[9px]">{msg.status === 'READ' ? '✓✓' : '✓'}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {typing && (
                    <div className="text-xs text-slate-400 italic">{typing} is typing...</div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Rating prompt for closed chats */}
                {activeChat?.status === 'CLOSED' && !activeChat?.rating && !showRating && (
                  <div className="p-2 border-t bg-slate-50 text-center">
                    <button onClick={() => setShowRating(true)} className="text-sm text-[#005DE8] font-medium hover:underline">
                      Rate this conversation
                    </button>
                  </div>
                )}

                {showRating && (
                  <div className="p-3 border-t bg-slate-50 space-y-2">
                    <p className="text-xs text-slate-500 text-center">How was your experience?</p>
                    <div className="flex justify-center gap-1">
                      {[1,2,3,4,5].map(s => (
                        <button key={s} onClick={() => setRating(s)}>
                          <Star className={cn('h-6 w-6', s <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300')} />
                        </button>
                      ))}
                    </div>
                    <Input placeholder="Any feedback? (optional)" value={feedback} onChange={e => setFeedback(e.target.value)} className="text-xs h-8" />
                    <Button size="sm" onClick={handleRate} disabled={!rating} className="w-full bg-[#005DE8] text-xs h-8">Submit</Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input */}
          {activeChat && activeChat.status !== 'CLOSED' && (
            <div className="p-2 border-t flex gap-1.5">
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx" />
              <Button
                variant="ghost" size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="h-9 w-9 p-0 shrink-0"
                data-testid="chat-attach-button"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </Button>
              <Input
                placeholder="Type a message..."
                value={newMessage}
                onChange={e => { setNewMessage(e.target.value); handleTyping(); }}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
                className="flex-1 text-sm h-9"
                data-testid="chat-message-input"
              />
              <Button
                size="sm"
                onClick={handleSend}
                disabled={!newMessage.trim()}
                className="bg-[#005DE8] h-9 w-9 p-0"
                data-testid="chat-send-button"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
